import { useState } from 'react';
import { Edit2, MessageQuestion, Trash } from 'iconsax-react';

import ConfirmationDeleteDialog from '@shared/components/reusables/confrimation-delete-dialog';

import { useKnowledgeBaseFilters } from '../hooks/use-knowledge-base-filters';
import {
  knowledgeFaqsApi,
  type KnowledgeFaq,
} from '../services/knowledge-content-api';
import { formatKnowledgeDate } from '../utils/format-date';
import { ContentCard } from './content-card';
import { ContentList } from './content-list';
import { ContentToolbar } from './content-toolbar';
import { FaqDialog } from './faq-dialog';

export function FaqsTab({
  kbaseId,
  softActions = false,
}: {
  kbaseId: number;
  softActions?: boolean;
}) {
  const { search } = useKnowledgeBaseFilters();
  const [editing, setEditing] = useState<KnowledgeFaq | null | 'new'>(null);
  const [deleting, setDeleting] = useState<KnowledgeFaq | null>(null);

  const list = knowledgeFaqsApi.useFetchInfinite({
    kbase: kbaseId,
    ...(search && { q: search }),
    page_size: 25,
  });
  const remove = knowledgeFaqsApi.useDelete();

  return (
    <>
      <ContentToolbar
        placeholder={softActions ? 'Search in FAQs' : 'Search in your faqs'}
        addLabel='Add FAQ'
        addVariant={softActions ? 'primaryPale' : 'primary'}
        onAdd={() => setEditing('new')}
      />
      <ContentList
        query={list}
        searching={Boolean(search)}
        emptyIcon={<MessageQuestion size={24} />}
        emptyTitle='No FAQs yet'
        emptyDescription='Add the questions customers ask most, with the answer you want given every time.'
        renderItem={(faq) => (
          <ContentCard
            key={faq.id}
            data-faq-id={faq.id}
            title={faq.question}
            meta={formatKnowledgeDate(faq.created_at)}
            menu={[
              {
                label: 'Edit',
                icon: <Edit2 size={16} />,
                onSelect: () => setEditing(faq),
              },
              {
                label: 'Delete',
                icon: (
                  <Trash
                    size={16}
                    className='text-red-500'
                  />
                ),
                destructive: true,
                onSelect: () => setDeleting(faq),
              },
            ]}
          >
            <p className='whitespace-pre-line text-[15px] text-gray-800'>
              {faq.answer}
            </p>
          </ContentCard>
        )}
      />

      <FaqDialog
        kbaseId={kbaseId}
        faq={editing === 'new' ? null : editing}
        open={editing !== null}
        onOpenChange={(open) => !open && setEditing(null)}
      />
      <ConfirmationDeleteDialog
        open={deleting !== null}
        onOpenChange={(open) => !open && setDeleting(null)}
        cancelHandler={() => setDeleting(null)}
        confirmationHandler={() => {
          if (!deleting) return;
          remove.mutate(
            { id: deleting.id },
            { onSuccess: () => setDeleting(null) }
          );
        }}
      />
    </>
  );
}
